import { StyledGrid } from '@Utils/styles';
import {
	useGlobalization,
	StylovyzeSearchBar,
	ExportButtonWithDialogAndText,
	useCompanyDateTime,
	DataGridButton,
	DataGridButtonType,
	useSettings,
} from '@innovyze/stylovyze';
import { Grid, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import React from 'react';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';
import selectSearchValue from '@Selectors/util.selectors';
import { changeSearch, reloadRehabResult } from '@Actions/index';
import { useRehabResultSelectors } from './useRehabResultsSelectors';
import qs from 'qs';

interface TableToolbarProps {
	treeId: string;
	disable: boolean;
	hasFullAccess: boolean;
	showFilterPanel: () => void;
	refreshList: () => void;
	filterRef: React.RefObject<HTMLButtonElement>;
	lastRunCompleted?: string;
}

export const TableToolbar = ({
	treeId,
	disable,
	hasFullAccess,
	showFilterPanel,
	refreshList,
	lastRunCompleted,
	filterRef,
}: TableToolbarProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const { formatDate } = useCompanyDateTime();
	const searchValue = selectSearchValue();
	const { tablePage, queryString } = useRehabResultSelectors(false);
	const { companySettings } = useSettings();

	const requestParams = {
		...qs.parse(queryString.substring(1)),
		slice: '100000',
		treeId,
		companySettings,
	};

	const csvLimit = '100000';
	// TODO
	//useIsFeatureEnabled('info-360-risk-csv-export-limit') ?? '100000';

	const dispatchSearchFn = (text: string) =>
		dispatch(changeSearch({ search: text, forGroupResults: false }));

	const lastRunCompletedDiv = () => {
		if (lastRunCompleted && lastRunCompleted.length != 0)
			return (
				<div style={{ paddingTop: '6px' }}>
					{t('Results below were created on {{datetime}}', {
						datetime: formatDateTimeWithFormatDate(
							lastRunCompleted,
							formatDate,
						),
					})}
				</div>
			);
		else return [];
	};

	return (
		<StyledGrid
			alignItems="center"
			justifyContent="space-between"
			container
			style={{
				width: '100%',
				marginTop: '1rem',
				marginBottom: '1rem',
			}}>
			<Grid container xs={4}>
				<Stack>
					<StylovyzeSearchBar
						onChange={(query: string) => {
							dispatchSearchFn(query);
							dispatch(reloadRehabResult());
						}}
						onRequestSearch={() => {
							dispatch(reloadRehabResult());
						}}
						onCancelSearch={() => {
							dispatchSearchFn('');
							dispatch(reloadRehabResult());
						}}
						placeholder={t('Search')}
						value={searchValue}
						dataCy={'search-01'}></StylovyzeSearchBar>
					{lastRunCompletedDiv()}
				</Stack>
			</Grid>
			<Grid
				item
				xs={8}
				container
				alignItems="center"
				justifyContent="flex-end"
				data-cy="grid-actionbutton"
				wrap="nowrap">
				<DataGridButton
					type={DataGridButtonType.FILTER}
					onClick={() => showFilterPanel()}
					disabled={disable}
					data-cy="filterButton"
					ref={filterRef}
				/>
				<DataGridButton
					type={DataGridButtonType.REFRESH}
					onClick={refreshList}
					disabled={disable}
					data-cy="refreshButton"
				/>
				{hasFullAccess && (
					<ExportButtonWithDialogAndText
						total={tablePage.total}
						requestParams={requestParams as Record<string, string>}
						disabled={disable}
						source={'risk.export.create'}
						title={t('Export')}
						maxRows={+csvLimit}
						dialogDataCy="export-dialog"
						iconDataCy="export-icon"
						maxPollAttempts={60}
						iterationTime={5000}
					/>
				)}
			</Grid>
		</StyledGrid>
	);
};
